import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import { SKILL_BY_ID, labelFor } from '../../../data/skillOntology';
import {
  FREE_COURSE_PROVIDERS,
  relevantProvidersFor,
  categoryForSkill,
} from '../../../data/freeCourseProviders';
import {
  suggestCourseQueriesForSkills,
  isLlmCourseSuggesterAvailable,
} from '../../../services/aiCourseQuerySuggester';

/**
 * FreeCoursesPanel
 * --------------------------------------------------------------------------
 * Two-tier UI for finding free learning content for missing skills:
 *
 *   1. Always-on: deep-links to provider search pages (Class Central,
 *      Coursera/edX free filter, YouTube, MIT OCW, etc.) using the skill
 *      label as the search query. Works without any API or LLM.
 *
 *   2. If the Anthropic API is wired (VITE_ANTHROPIC_API_KEY): we also
 *      call Claude to get 2-3 targeted search queries per skill, and
 *      render each as a deep-link to Class Central. This way the LLM
 *      contributes "what to look for"; the platform contributes "what
 *      really exists." Hallucination-safe by design, we never render
 *      a course name as a clickable link, only as a search query.
 *
 * If the LLM is not configured, the section just shows the always-on
 * provider deep-links and a small "Enable AI suggestions" hint.
 */

const ProviderLink = ({ provider, query }) => {
  const { t } = useTranslation();
  return (
    <a
      href={provider.buildUrl(query)}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-start gap-3 p-3 border border-border rounded-md bg-card hover:bg-muted/40 hover:border-primary/30 transition-smooth"
    >
      <div className={`w-8 h-8 rounded-md ${provider.color} text-white flex items-center justify-center shrink-0`}>
        <Icon name={provider.icon} size={14} color="white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-foreground truncate">{provider.name}</p>
          <span className="text-[10px] font-bold uppercase tracking-wide text-primary px-1.5 py-0.5 rounded bg-primary/10 shrink-0">
            {t(provider.badgeKey)}
          </span>
        </div>
        <p className="text-xs text-muted-foreground leading-snug mt-0.5">{t(provider.descKey)}</p>
      </div>
      <Icon name="ExternalLink" size={14} className="text-muted-foreground shrink-0 mt-1" />
    </a>
  );
};

const SmartQueryLink = ({ query, why }) => (
  <a
    href={`https://www.classcentral.com/search?q=${encodeURIComponent(query)}`}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-start gap-2 p-2.5 border border-primary/20 rounded-md bg-primary/5 hover:bg-primary/10 transition-smooth"
  >
    <Icon name="Search" size={14} className="text-primary shrink-0 mt-0.5" />
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-foreground leading-tight break-words">"{query}"</p>
      {why && <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{why}</p>}
    </div>
    <Icon name="ExternalLink" size={12} className="text-muted-foreground shrink-0 mt-1" />
  </a>
);

const FreeCoursesPanel = ({ missingSkillIds, lang }) => {
  const { t } = useTranslation();
  const [llmSuggestions, setLlmSuggestions] = useState({});
  const [isLoadingLlm, setIsLoadingLlm] = useState(false);
  const llmAvailable = isLlmCourseSuggesterAvailable();

  // Skills with valid ontology entry
  const skills = missingSkillIds
    .map((id) => ({ id, skill: SKILL_BY_ID[id] }))
    .filter((x) => x.skill);

  // Fire LLM suggestions on mount if available
  useEffect(() => {
    let cancelled = false;
    if (!llmAvailable || skills.length === 0) return;

    setIsLoadingLlm(true);
    const payload = skills.map((x) => ({
      id: x.id,
      label: labelFor(x.skill, lang),
    }));
    suggestCourseQueriesForSkills(payload)
      .then((res) => {
        if (!cancelled) setLlmSuggestions(res || {});
      })
      .finally(() => {
        if (!cancelled) setIsLoadingLlm(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missingSkillIds.join(',')]);

  if (skills.length === 0) return null;

  return (
    <div className="mt-6 pt-5 border-t border-border">
      <div className="flex items-center gap-2 mb-1">
        <Icon name="Compass" size={18} className="text-primary" />
        <h4 className="font-semibold text-foreground">{t('employeePortal.profile.freeCoursesTitle')}</h4>
        {llmAvailable && (
          <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-primary/10 text-primary rounded border border-primary/20">
            {isLoadingLlm
              ? t('employeePortal.profile.aiSuggestingLoading')
              : t('employeePortal.profile.aiAssisted')}
          </span>
        )}
      </div>
      <p className="text-sm text-muted-foreground mb-4">{t('employeePortal.profile.freeCoursesSubtitle')}</p>

      {!llmAvailable && (
        <div className="mb-4 p-3 bg-muted/30 border border-border rounded-md flex items-start gap-2 text-xs text-muted-foreground">
          <Icon name="Sparkles" size={13} className="shrink-0 mt-0.5" />
          <span>{t('employeePortal.profile.llmUnavailableHint')}</span>
        </div>
      )}

      <div className="space-y-5">
        {skills.map(({ id, skill }) => {
          const skillLabel = labelFor(skill, lang);
          const cat = categoryForSkill(id);
          const providers = relevantProvidersFor(cat);
          const llm = llmSuggestions[id] || [];

          return (
            <div key={id}>
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded border bg-warning/10 text-warning border-warning/30">
                  <Icon name="Target" size={11} />
                  {skillLabel}
                </span>
              </div>

              {/* LLM-suggested queries, rendered as deep-links to Class Central */}
              {llm.length > 0 && (
                <div className="ml-1 mb-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                    {t('employeePortal.profile.aiQueriesLabel')}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {llm.map((s, i) => <SmartQueryLink key={i} query={s.query} why={s.why} />)}
                  </div>
                </div>
              )}

              {/* Always-on provider deep-links */}
              <div className="ml-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                  {t('employeePortal.profile.providerSearchLabel')}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {providers.map((p) => (
                    <ProviderLink key={p.id} provider={p} query={skillLabel} />
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-5 flex items-start gap-2 text-xs text-muted-foreground">
        <Icon name="Info" size={13} className="shrink-0 mt-0.5" />
        <span>{t('employeePortal.profile.freeCoursesDisclaimer')}</span>
      </div>
    </div>
  );
};

export default FreeCoursesPanel;
